<template>
	<div
		:class="['px-4 mt-2', headerClass]"
		:id="id"
		v-if="isInHeader"
	>
		<button
			@click="openModal"
			class="bg-white px-4 py-2 w-full rounded-full flex items-center justify-between"
		>
			<span class="text-sm font-regular text-slate-500 text-left italic">
				Enter ticker (e.g, AAPL)
			</span>

			<svg
				width="18"
				height="19"
				viewBox="0 0 18 19"
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
			>
				<path
					d="M15.75 16.25L12.4875 12.9875M14.25 8.75C14.25 12.0637 11.5637 14.75 8.25 14.75C4.93629 14.75 2.25 12.0637 2.25 8.75C2.25 5.43629 4.93629 2.75 8.25 2.75C11.5637 2.75 14.25 5.43629 14.25 8.75Z"
					stroke="#475569"
					stroke-width="2"
					stroke-linecap="round"
					stroke-linejoin="round"
				/>
			</svg>
		</button>
	</div>
	<button
		v-else-if="isIcon"
		aria-label="Search for post"
		class="h-[24px] w-[24px] px-0 py-0 ml-auto mr-[12px] xl:mx-[24px] search-btn"
		:class="headerClass"
		@click="openModal"
	>
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 25"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M10.0799 1.93994C5.56867 1.93994 1.91992 5.58869 1.91992 10.0999C1.91992 14.6112 5.56867 18.2599 10.0799 18.2599C11.6905 18.2599 13.1812 17.7874 14.4449 16.9849L20.3399 22.8799L22.3799 20.8399L16.5599 15.0349C17.6062 13.6624 18.2399 11.9618 18.2399 10.0999C18.2399 5.58869 14.5912 1.93994 10.0799 1.93994ZM10.0799 3.85994C13.5355 3.85994 16.3199 6.64432 16.3199 10.0999C16.3199 13.5556 13.5355 16.3399 10.0799 16.3399C6.6243 16.3399 3.83992 13.5556 3.83992 10.0999C3.83992 6.64432 6.6243 3.85994 10.0799 3.85994Z"
				fill="white"
			/>
		</svg>
	</button>

	<div
		v-else
		class="flex flex-col gap-4 border rounded-xl p-4 border border-slate-100"
		:class="headerClass"
		:id="id"
	>
		<h3 class="text-xl font-regular text-black title-font font-bold">
			Quote Finder
		</h3>

		<div class="relative">
			<div class="relative">
				<input
					type="text"
					v-model="searchQuery"
					placeholder="Enter ticker (e.g. AAPL)"
					class="flex max-w-xl w-full h-10 mx-auto justify-between items-center bg-slate-50 rounded-xl pl-4 pr-10 text-slate-700 font-regular focus:outline-none focus:ring-2 focus:ring-blue-300"
				/>

				<button
					v-if="searchQuery && results.length > 0"
					@click="searchQuery = ''"
					class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
					aria-label="Clear search"
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M18 6L6 18M6 6l12 12"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
						/>
					</svg>
				</button>
			</div>

			<div
				v-if="searchQuery && results.length > 0"
				class="max-h-[244px] min-h-[244px] h-full overflow-y-auto absolute top-12 -left-4 right-0 bg-white rounded-bl-lg rounded-br-lg z-10 no-scrollbar"
				style="width: calc(100% + 2rem)"
			>
				<NuxtLink
					v-for="result in results"
					:key="result.symbol"
					:to="`/quote/${result.symbol}/`"
					class="flex items-center px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
				>
					<span
						v-if="failedImages.has(result.symbol)"
						class="w-10 h-10 rounded-md mr-4 flex items-center justify-center text-white text-sm font-medium"
						style="background-color: #222"
					>
						{{ result.symbol }}
					</span>
					<img
						v-else
						:src="`https://stt-ts-resources.s3.us-east-1.amazonaws.com/stock-symbols/${result.symbol}`"
						:alt="`Logo image for ${result.symbol}`"
						class="w-10 h-10 rounded-lg object-cover mr-4 bg-slate-100/[.2]"
						@error="handleImageError(result.symbol)"
					/>
					<div class="flex-1">
						<div class="flex items-center gap-2">
							<span class="text-base font-semibold text-slate-900">
								{{ result.symbol }}
							</span>
							<span class="text-sm text-slate-500">
								{{ result.longName }}
							</span>
						</div>
					</div>
					<span class="text-sm text-slate-400">
						{{ result.exchange }}
					</span>
				</NuxtLink>
			</div>
		</div>
		<template v-if="!disablePreview && previewStocks.length > 0">
			<div
				class="grid lg:grid-cols-2 gap-3"
				style="margin: 0 -16px"
			>
				<NuxtLink
					v-for="stock in previewStocks"
					:key="stock.SYMBOL"
					:to="`/quote/${stock.SYMBOL}/`"
				>
					<StockBlock
						:key="stock.SYMBOL"
						:symbol="stock.SYMBOL"
						:net_chg="stock.NET_CHG"
						:price="stock.BID"
						:pc_chg="stock.PC_CHG"
						:name="stock.NAME"
						:labels="stock.labels || []"
						:prices="stock.prices || []"
					/>
				</NuxtLink>
			</div>
		</template>
	</div>

	<SearchBar
		v-if="showModal"
		ref="searchBar"
		:close-modal="closeModal"
		:on-type="onType"
		:results="results"
		:is-in-quote-page="props.isInQuotePage"
		:default-search-type="props.defaultSearchType"
	></SearchBar>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from "vue";
import Fuse from "fuse.js";
import SearchBar from "../search-bar.vue";
import StockBlock from "@/components/stocks/stock-block.vue";
import { useStocksStore } from "~/stores/useStocksStore";
import { useDeferredSymbolsInit } from "~/composables/useDeferredSymbolsInit";
import { normalizeExchange } from "~/utils/exchange-normalizer";

const props = defineProps<{
	isInHeader: boolean;
	isIcon: boolean;
	headerClass?: string;
	isInQuotePage?: boolean;
	defaultSearchType?: string;
	disablePreview?: boolean;
	id?: string;
}>();

interface SearchResult {
	symbol: string;
	longName: string;
	image?: string;
	title?: string;
	slug?: string;
	post_type?: string;
	[key: string]: any;
}

interface SearchBarRef {
	$el: {
		querySelector: (selector: string) => HTMLElement | null;
	};
}

const showModal = ref(false);
const searchBar = ref<SearchBarRef | null>(null);
const results = ref<SearchResult[]>([]);
const fuse = ref<Fuse<SearchResult> | null>(null);
const visibleCount = ref(20);
const searchQuery = ref("");
const failedImages = ref(new Set<string>());

const stocksStore = useStocksStore();
const { eagerInit: eagerInitSymbols } = useDeferredSymbolsInit();

const previewStocks = computed(() =>
	stocksStore.top_gainers_stocks.slice(0, 4)
);

const handleImageError = (symbol: string) => {
	failedImages.value.add(symbol);
};

function openModal() {
	eagerInitSymbols();
	showModal.value = true;
	document.body.style.overflow = "hidden";
	setTimeout(() => {
		searchBar.value?.$el?.querySelector("input")?.focus();
	}, 100);
}

function closeModal() {
	showModal.value = false;
	results.value = [];
	document.body.style.overflow = "";
}

function handleKeyDown(e: KeyboardEvent) {
	if (e.key === "Escape") {
		closeModal();
	}
}

const debouncedOnType = debounce((q: string) => {
	if (!q || !fuse.value) {
		results.value = [];
		return;
	}

	try {
		const searchResults = fuse.value.search(q);

		results.value = searchResults
			.map(({ item }) => {
				// Normalize the exchange using the utility function
				item.exchange = normalizeExchange(item.exchange);
				return item;
			})
			.slice(0, visibleCount.value);
	} catch (error) {
		console.error("Error in fuse search:", error);
		results.value = [];
	}
}, 300);

const onType = (q: string) => {
	debouncedOnType(q);
};

function debounce(fn: Function, delay: number) {
	let timeoutId: ReturnType<typeof setTimeout>;
	return function (...args: any[]) {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
		timeoutId = setTimeout(() => {
			fn(...args);
		}, delay);
	};
}

onMounted(() => {
	document.addEventListener("keydown", handleKeyDown);
	if (!props.disablePreview && stocksStore.top_gainers_stocks.length === 0) {
		stocksStore.fetchTopGainerStocks(20);
	}
});

onBeforeUnmount(() => {
	document.removeEventListener("keydown", handleKeyDown);
});

watch(
	() => stocksStore.symbols_list,
	(newData, _) => {
		if (import.meta.client && newData && newData.length > 0) {
			// Transform the data to match the expected format
			const searchData = newData.map((item: any) => ({
				symbol: item.SYMBOL || item.symbol,
				longName: item.NAME || item.name || item.longName,
				exchange: normalizeExchange(item.exchange || ""),
				...item
			}));

			fuse.value = new Fuse(searchData, {
				keys: ["symbol", "longName", "NAME", "SYMBOL"],
				threshold: 0.3,
				includeScore: false,
				findAllMatches: true,
				isCaseSensitive: false
			});
		} else {
			console.error("No symbols data available:", newData);
		}
	},
	{
		deep: true,
		immediate: true
	}
);

// Watch searchQuery and update results
watch(searchQuery, (newQuery) => {
	if (!newQuery || newQuery.trim().length < 1) {
		results.value = [];
		return;
	}

	if (!fuse.value) {
		return;
	}

	try {
		const searchResults = fuse.value.search(newQuery);
		results.value = searchResults.map(({ item }) => item).slice(0, 10);
	} catch (error) {
		console.error("Error in fuse search:", error);
		results.value = [];
	}
});
</script>

<style scoped>
.no-scrollbar {
	-ms-overflow-style: none; /* IE and Edge */
	scrollbar-width: none; /* Firefox */
}

.no-scrollbar::-webkit-scrollbar {
	display: none; /* Chrome, Safari and Opera */
}
</style>
